// =====================================
// USEORDERS.JSX - FUNCIONES ALINEADAS CON SUPABASE
// =====================================

import { useCallback, useState } from "react";
import {
  transformRefillDataForSupabase,
  validateOrderData,
  ORDER_STATUS,
  PAYMENT_STATUS,
  DETAIL_TYPE,
  ORDER_DETAIL_STATUS,
} from "../data/dataStructures";
import supabase from "../SupaCredentials";

// =====================================
// 1. FUNCIÓN CORREGIDA PARA NEW REFILLS
// =====================================
const createBulkRefillOrder = async (
  items,
  groupedByAddress,
  totals,
  notes = ""
) => {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("Usuario no autenticado");

  try {
    console.log(
      "🔄 Iniciando createBulkRefillOrder con",
      items.length,
      "items"
    );

    // 1. VERIFICAR DISPONIBILIDAD DE BOTELLONES REFILL_BASE
    console.log("🔍 Verificando disponibilidad de botellones refill_base...");

    const { data: availableRefillJugs, error: availabilityError } =
      await supabase
        .from("jugs")
        .select("id")
        .eq("operational_state", "refill_base")
        .is("current_user_id", null)
        .order("created_at", { ascending: true }); // Los más antiguos primero

    if (availabilityError) {
      console.error("❌ Error verificando disponibilidad:", availabilityError);
      throw availabilityError;
    }

    const requiredJugs = items.length;
    const availableCount = availableRefillJugs?.length || 0;

    console.log(
      `📊 Requeridos: ${requiredJugs}, Disponibles: ${availableCount}`
    );

    if (availableCount < requiredJugs) {
      const errorMsg = `No hay suficientes botellones disponibles para recambio. Se necesitan ${requiredJugs} pero solo hay ${availableCount} disponibles.`;
      console.error("❌", errorMsg);
      return {
        success: false,
        error: errorMsg,
        requiredJugs,
        availableCount,
      };
    }

    console.log("✅ Suficientes botellones disponibles");

    // 2. OBTENER PRODUCTO DE REFILL
    console.log("🔍 Obteniendo producto de refill...");

    const { data: refillProduct, error: productError } = await supabase
      .from("products")
      .select("*")
      .eq("is_refill", true)
      .eq("active", true)
      .single();

    if (productError || !refillProduct) {
      console.error("❌ Error obteniendo producto de refill:", productError);
      throw new Error("No se encontró el producto de refill");
    }

    console.log("✅ Producto de refill:", refillProduct.name);

    // 3. CREAR LA ORDEN PRINCIPAL
    const scheduledDate = new Date();
    scheduledDate.setDate(scheduledDate.getDate() + 2);

    const orderData = {
      user_id: user.id,
      order_status: ORDER_STATUS.PENDING,
      payment_status: PAYMENT_STATUS.PENDING,
      subtotal: totals.subtotal,
      shipping_cost: totals.deliveryFee,
      total: totals.total,
      notes: notes || null,
      created_by: user.id,
    };

    console.log("🔄 Creando orden principal...");

    const { data: order, error: orderError } = await supabase
      .from("orders")
      .insert(orderData)
      .select()
      .single();

    if (orderError) {
      console.error("❌ Error creando orden:", orderError);
      throw orderError;
    }

    console.log("✅ Orden creada:", order.short_id);

    // 3.5 RESERVAR LOS BOTELLONES NECESARIOS
    const reservedJugs = availableRefillJugs.slice(0, requiredJugs);
    console.log("🔒 Reservando", reservedJugs.length, "botellones");

    // Crear un mapa para asignar jugs a order_details
    const jugAssignments = new Map();
    let jugIndex = 0;

    items.forEach((item) => {
      jugAssignments.set(item.jugId, reservedJugs[jugIndex].id);
      jugIndex++;
    });

    // 4. CREAR ORDER_DETAILS CON DELIVERED_JUG_ID
    const orderDetails = [];

    for (const addressGroup of groupedByAddress) {
      for (const item of addressGroup.items) {
        const assignedJugId = jugAssignments.get(item.jugId);

        orderDetails.push({
          order_id: order.id,
          product_id: refillProduct.id,
          quantity: 1,
          unit_price: refillProduct.unit_price,
          detail_type: DETAIL_TYPE.REFILL,
          returned_jug_id: item.jugId,
          delivered_jug_id: assignedJugId, // ✅ Asignado desde el inicio
          delivery_address_id: addressGroup.addressId,
          scheduled_delivery_date: scheduledDate,
          status: ORDER_DETAIL_STATUS.RECENTLY_ORDERED,
          created_by: user.id,
        });
      }
    }

    console.log("🔄 Insertando order_details:", orderDetails.length, "items");

    const { data: details, error: detailsError } = await supabase
      .from("order_details")
      .insert(orderDetails)
      .select();

    if (detailsError) {
      console.error("❌ Error creando order_details:", detailsError);
      throw detailsError;
    }

    console.log("✅ Order_details creados:", details.length);

    // 5. ACTUALIZAR ESTADOS DE BOTELLONES

    // 5.1 Botellones a devolver (pending_return)
    // const returningJugIds = items.map((item) => item.jugId);

    // console.log(
    //   "🔄 Actualizando",
    //   returningJugIds.length,
    //   "botellones a pending_return"
    // );

    // const { error: returningError } = await supabase
    //   .from("jugs")
    //   .update({
    //     operational_state: "pending_return",
    //     updated_at: new Date().toISOString(),
    //   })
    //   .in("id", returningJugIds);

    // if (returningError) {
    //   console.error(
    //     "⚠️ Error actualizando botellones a devolver:",
    //     returningError
    //   );
    // } else {
    //   console.log("✅ Botellones marcados para devolución");
    // }

    // 5.2 Botellones nuevos a entregar (pending_delivery)
    const deliveringJugIds = reservedJugs.map((j) => j.id);

    console.log(
      "🔄 Actualizando",
      deliveringJugIds.length,
      "botellones a pending_delivery"
    );

    const { error: deliveringError } = await supabase
      .from("jugs")
      .update({
        operational_state: "recently_ordered",
        current_user_id: user.id,
        updated_at: new Date().toISOString(),
      })
      .in("id", deliveringJugIds);

    if (deliveringError) {
      console.error(
        "⚠️ Error actualizando botellones a entregar:",
        deliveringError
      );
    } else {
      console.log("✅ Botellones asignados para entrega");
    }

    // 5.3 Establecer previous_jug_id en los nuevos botellones
    // console.log("🔄 Estableciendo relaciones previous_jug_id...");

    // const updatePromises = [];
    // for (const [returnedId, deliveredId] of jugAssignments) {
    //   updatePromises.push(
    //     supabase
    //       .from("jugs")
    //       .update({ previous_jug_id: returnedId })
    //       .eq("id", deliveredId)
    //   );
    // }

    // await Promise.all(updatePromises);
    // console.log("✅ Relaciones previous_jug_id establecidas");

    // 6. PREPARAR RESPUESTA
    console.log("🎉 createBulkRefillOrder completado exitosamente");

    return {
      success: true,
      order,
      details,
      summary: {
        orderId: order.id,
        orderShortId: order.short_id,
        itemCount: items.length,
        total: totals.total,
        deliveryDate: scheduledDate,
        refillPairs: Array.from(jugAssignments.entries()).map(
          ([returned, delivered]) => ({
            returned_jug_id: returned,
            delivered_jug_id: delivered,
          })
        ),
      },
    };
  } catch (error) {
    console.error("❌ Error en createBulkRefillOrder:", error);

    // Si hay un error y ya se creó la orden, considerar eliminarla
    // para mantener consistencia (opcional)

    return {
      success: false,
      error: error.message || "Error al crear orden de recambio",
      details: error,
    };
  }
};

// =====================================
// MÉTODO AUXILIAR: Asignar jugs del inventario
// =====================================
const assignJugsToNewOrder = async (quantity, orderId, userId) => {
  console.log(
    `🔄 Asignando ${quantity} jugs del inventario para orden ${orderId}`
  );

  // 1. Buscar jugs disponibles en inventario
  const { data: availableJugs, error: jugsError } = await supabase
    .from("jugs")
    .select("id, barcode, physical_state")
    .eq("operational_state", "in_inventory") // ✅ Corregido: usar operational_state
    .eq("active", true)
    .order("created_at", { ascending: true }) // FIFO - los más antiguos primero
    .limit(quantity);

  if (jugsError) {
    console.error("❌ Error consultando jugs en inventario:", jugsError);
    throw jugsError;
  }

  if (!availableJugs || availableJugs.length < quantity) {
    throw new Error(
      `No hay suficientes botellones en inventario. Necesarios: ${quantity}, Disponibles: ${
        availableJugs?.length || 0
      }`
    );
  }

  console.log(`✅ Encontrados ${availableJugs.length} jugs disponibles`);

  // 2. Actualizar el estado de los jugs a 'recently_ordered' y asignar usuario
  const jugIds = availableJugs.map((jug) => jug.id);

  const { error: updateError } = await supabase
    .from("jugs")
    .update({
      operational_state: "recently_ordered", // ✅ Corregido: usar operational_state
      current_user_id: userId, // ✅ NUEVO: Asignar el usuario actual
      updated_at: new Date().toISOString(),
    })
    .in("id", jugIds);

  if (updateError) {
    console.error("❌ Error actualizando estado de jugs:", updateError);
    throw updateError;
  }

  console.log(`✅ ${jugIds.length} jugs actualizados y asignados al usuario`);

  return availableJugs;
};

// =====================================
// MÉTODO AUXILIAR: Asignar jugs a refills
// =====================================
const assignRefillJugs = async (orderDetails, orderId, userId) => {
  try {
    // 1. Obtener la cantidad necesaria
    const refillCount = orderDetails.length;

    // 2. Buscar botellones disponibles para refill
    const { data: availableJugs, error } = await supabase
      .from("jugs")
      .select("id")
      .eq("operational_state", "refill_base")
      .is("current_user_id", null)
      .limit(refillCount);

    if (error) throw error;

    if (!availableJugs || availableJugs.length < refillCount) {
      throw new Error(
        `Insuficientes botellones para refill. Necesarios: ${refillCount}, Disponibles: ${
          availableJugs?.length || 0
        }`
      );
    }

    // 3. Asignar cada botellón a un order_detail
    const assignments = [];
    for (let i = 0; i < orderDetails.length; i++) {
      const detail = orderDetails[i];
      const jug = availableJugs[i];

      // Actualizar el order_detail con el delivered_jug_id
      const { error: updateError } = await supabase
        .from("order_details")
        .update({ delivered_jug_id: jug.id })
        .eq("id", detail.id);

      if (updateError) throw updateError;

      // Actualizar el botellón
      const { error: jugError } = await supabase
        .from("jugs")
        .update({
          operational_state: "pending_delivery",
          current_user_id: userId,
          previous_jug_id: detail.returned_jug_id, // Enlazar con el botellón que se devuelve
          updated_at: new Date().toISOString(),
        })
        .eq("id", jug.id);

      if (jugError) throw jugError;

      assignments.push({
        order_detail_id: detail.id,
        delivered_jug_id: jug.id,
        returned_jug_id: detail.returned_jug_id,
      });
    }

    return assignments;
  } catch (error) {
    console.error("❌ Error asignando jugs para refill:", error);
    throw error;
  }
};

// =====================================
// 2. FUNCIÓN MEJORADA PARA NEW ITEMS
// =====================================
export const createNewItemsOrder = async (orderData, user) => {
  console.log("🔄 createNewItemsOrder: Iniciando con datos:", orderData);

  try {
    const {
      selectedProducts,
      selectedAddressId,
      selectedDeliveryDate,
      selectedTimeSlot,
      totals,
    } = orderData;

    // 1. Verificar si hay productos seleccionados
    const hasJugs = selectedProducts?.jugs > 0;
    const hasDispensers = selectedProducts?.dispensers > 0;

    if (!hasJugs && !hasDispensers) {
      throw new Error("No hay productos seleccionados");
    }

    // 2. Validar que tenemos un addressId válido
    if (!selectedAddressId) {
      throw new Error("No se ha seleccionado una dirección de entrega");
    }

    // 3. Verificar que la dirección existe y pertenece al usuario
    const { data: addressData, error: addressError } = await supabase
      .from("addresses")
      .select("id")
      .eq("id", selectedAddressId)
      .eq("user_id", user.id)
      .single();

    if (addressError || !addressData) {
      console.error("❌ Error verificando dirección:", addressError);
      throw new Error("La dirección seleccionada no es válida");
    }

    // 4. Crear la orden principal
    const orderPayload = {
      user_id: user.id,
      order_status: ORDER_STATUS.PENDING,
      payment_status: PAYMENT_STATUS.PENDING,
      subtotal: totals.subtotal,
      shipping_cost: totals.deliveryFee || 0,
      total: totals.total,
      notes: `Pedido nuevo - Entrega: ${selectedDeliveryDate.toLocaleDateString(
        "es-CO"
      )} - ${selectedTimeSlot === "morning" ? "Mañana" : "Tarde"}`,
      created_by: user.id,
    };

    const { data: order, error: orderError } = await supabase
      .from("orders")
      .insert([orderPayload])
      .select()
      .single();

    if (orderError) throw orderError;

    console.log("✅ Orden principal creada:", order.short_id);

    // 5. Asignar botellones del inventario
    let assignedJugs = [];
    if (hasJugs) {
      try {
        assignedJugs = await assignJugsToNewOrder(
          selectedProducts.jugs,
          order.id,
          user.id
        );
        console.log(`✅ ${assignedJugs.length} jugs asignados exitosamente`);
      } catch (jugError) {
        // Si falla la asignación, eliminar la orden
        await supabase.from("orders").delete().eq("id", order.id);
        throw jugError;
      }
    }

    // 6. Crear order_details
    const orderDetails = [];

    // ✅ CORREGIDO: Crear un order_detail por cada botellón asignado
    if (hasJugs && assignedJugs.length > 0) {
      // Obtener producto de botellón
      const { data: jugProduct, error: jugProductError } = await supabase
        .from("products")
        .select("*")
        .eq("id", "411f1cc9-5822-4453-a5a9-21a456d2289a") // ID del producto botellón
        .maybeSingle(); // ✅ Usar maybeSingle para evitar errores

      if (jugProductError || !jugProduct) {
        console.error(
          "❌ Error obteniendo producto botellón:",
          jugProductError
        );
        // Revertir jugs asignados
        if (assignedJugs.length > 0) {
          await supabase
            .from("jugs")
            .update({
              operational_state: "in_inventory",
              current_user_id: null,
            })
            .in(
              "id",
              assignedJugs.map((j) => j.id)
            );
        }
        // Eliminar orden
        await supabase.from("orders").delete().eq("id", order.id);
        throw new Error("No se pudo obtener información del producto botellón");
      }

      // ✅ IMPORTANTE: Crear un order_detail por cada jug
      for (const jug of assignedJugs) {
        orderDetails.push({
          order_id: order.id,
          product_id: jugProduct.id,
          quantity: 1, // ✅ Siempre 1 por cada jug
          unit_price: jugProduct.unit_price,
          detail_type: DETAIL_TYPE.NEW,
          delivered_jug_id: jug.id, // ✅ IMPORTANTE: Vincular el jug específico
          delivery_address_id: selectedAddressId,
          scheduled_delivery_date: selectedDeliveryDate,
          status: ORDER_DETAIL_STATUS.RECENTLY_ORDERED,
          created_by: user.id,
        });
      }
    }

    // Agregar dispensadores si hay
    if (hasDispensers) {
      // Obtener producto de dispensador
      const { data: dispenserProduct, error: dispenserProductError } =
        await supabase
          .from("products")
          .select("*")
          .eq("id", "db01f3a4-b59f-46e1-9d9b-228a617ad4a6") // ID del producto dispensador
          .maybeSingle();

      if (!dispenserProductError && dispenserProduct) {
        // Para dispensadores, sí podemos usar quantity
        orderDetails.push({
          order_id: order.id,
          product_id: dispenserProduct.id,
          quantity: selectedProducts.dispensers,
          unit_price: dispenserProduct.unit_price,
          detail_type: DETAIL_TYPE.NEW,
          delivery_address_id: selectedAddressId,
          scheduled_delivery_date: selectedDeliveryDate,
          status: ORDER_DETAIL_STATUS.RECENTLY_ORDERED,
          created_by: user.id,
        });
      }
    }

    // 7. Insertar todos los detalles
    if (orderDetails.length > 0) {
      const { data: details, error: detailsError } = await supabase
        .from("order_details")
        .insert(orderDetails)
        .select();

      if (detailsError) {
        console.error("❌ Error creando order_details:", detailsError);

        // Revertir cambios si falla
        if (assignedJugs.length > 0) {
          await supabase
            .from("jugs")
            .update({
              operational_state: "in_inventory",
              current_user_id: null,
            })
            .in(
              "id",
              assignedJugs.map((j) => j.id)
            );
        }
        // Eliminar orden
        await supabase.from("orders").delete().eq("id", order.id);

        throw detailsError;
      }

      console.log("✅ Order_details creados:", details.length);
    }

    // 8. Resultado final
    const result = {
      orderId: order.id,
      orderShortId: order.short_id,
      itemCount: assignedJugs.length + (selectedProducts.dispensers || 0),
      total: totals.total,
      deliveryDate: selectedDeliveryDate,
      assignedJugs: assignedJugs.map((j) => ({
        id: j.id,
        barcode: j.barcode,
      })),
    };

    console.log("🎉 Orden creada exitosamente:", result);

    return {
      success: true,
      order,
      summary: result,
    };
  } catch (error) {
    console.error("❌ Error en createNewItemsOrder:", error);
    throw error;
  }
};

// =====================================
// 3. FUNCIÓN PRINCIPAL DEL HOOK useOrders
// =====================================
export const useOrders = () => {
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [orderError, setOrderError] = useState(null);

  // Función unificada para crear órdenes
  // Función unificada para crear órdenes
  const createOrder = async (orderData, orderType = "new_items") => {
    setIsCreatingOrder(true);
    setOrderError(null);

    try {
      let result;

      if (orderType === "refill") {
        // CAMBIO: Detectar si los datos ya vienen transformados
        let dataForBulkRefill;

        // Si tiene la estructura nueva (ya transformada)
        if (
          orderData.items &&
          orderData.groupedByAddress &&
          orderData.scheduledDate
        ) {
          // Los datos ya están listos, solo agregar el usuario
          dataForBulkRefill = {
            ...orderData,
            user: orderData.user, // Asegurar que tenga usuario
          };
        }
        // Si tiene la estructura antigua (necesita transformación)
        else if (orderData.activeRefillItems && orderData.totals) {
          // Transformar datos antiguos
          dataForBulkRefill = transformRefillDataForSupabase(
            orderData,
            orderData.user,
            orderData.scheduledDate,
            orderData.timeSlot
          );
        }
        // Si no tiene ninguna estructura conocida
        else {
          throw new Error("Estructura de datos de refill no reconocida");
        }

        result = await createBulkRefillOrder(
          dataForBulkRefill.items, // ✅ Usar dataForBulkRefill
          dataForBulkRefill.groupedByAddress, // ✅ Usar dataForBulkRefill
          dataForBulkRefill.totals, // ✅ Usar dataForBulkRefill
          dataForBulkRefill.notes || "" // ✅ Usar dataForBulkRefill
        );
      } else {
        result = await createNewItemsOrder(orderData, orderData.user);
      }

      console.log("🎉 Orden creada exitosamente:", result.summary);
      return result;
    } catch (error) {
      console.error("❌ Error creando orden:", error);
      setOrderError(error.message);
      throw error;
    } finally {
      setIsCreatingOrder(false);
    }
  };

  // Función para obtener órdenes del usuario
  const getUserOrders = useCallback(async (userId = null) => {
    const targetUserId = userId;
    if (!targetUserId) return [];

    try {
      const { data: orders, error } = await supabase
        .from("orders")
        .select(
          `
          *,
          order_details (
            *,
            products (name, sku_code),
            addresses (address, city, details),
            jugs:delivered_jug_id (id, barcode)
          )
        `
        )
        .eq("user_id", targetUserId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return orders || [];
    } catch (error) {
      console.error("Error fetching user orders:", error);
      return [];
    }
  }, []);

  return {
    // Estados
    isCreatingOrder,
    orderError,

    // Funciones principales
    createOrder,
    getUserOrders,

    // Funciones específicas (si necesitas usarlas directamente)
    createBulkRefillOrder,
    createNewItemsOrder,
    assignJugsToNewOrder,

    // Función para limpiar errores
    clearOrderError: () => setOrderError(null),
  };
};
